import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react';
import {ApiError} from '../../api';
import type {CheckoutSession, Order} from '../../api/types';
import {useRouter} from '../../app/router';
import {ROUTES, href} from '../../app/routes';
import {useI18n, type MessageKey} from '../../i18n';
import {orderTotalTone, useOrder} from '../../order';
import {
  clearAttempt,
  isExpired,
  paymentOutcome,
  readAttempt,
  readAttemptFor,
  type CheckoutAttempt,
  type PaymentOutcomeKind,
} from '../../payment';
import {useApi, useGuestSession} from '../../session';
import {
  ActionLink,
  BackLink,
  Button,
  Card,
  Money,
  Screen,
  ScreenBody,
  ScreenFooter,
  SplitRow,
  Stack,
  TerminalNotice,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q9 · Payment — verification, route `/pay/:orderId/return`
 *
 * The screen a guest lands on after ReservePay, and the screen that has to tell
 * them the truth about their own money.
 *
 * ## The finding this screen is built around
 *
 * **A successful payment and an abandoned one look identical from here.**
 *
 * ReservePay's checkout object has no status field; the only progress signal it
 * carries is a payment id appearing, and that happens the moment the payer picks
 * a method — before any money moves. pokpok's webhook goes much further: it
 * verifies the HMAC, records the raw body, re-reads the checkout, finds the
 * payment through `list-payments`, confirms the status is exactly `SUCCEEDED`
 * and checks the amount — **and then stops one line short of settling**, because
 * every write path takes a credential and a webhook has no caller. The bill is
 * left ready to close, waiting for a human.
 *
 * So `CheckoutSessionState.succeeded` is written by nobody today, and neither is
 * `expired` or `cancelled`. A guest who paid in full gets `awaiting_payment`,
 * the same value as a guest who closed the tab.
 *
 * **This screen therefore does not promise that the bill is settled.** It says
 * what the guest did, what the system knows, and what happens next
 * (`q9.unconfirmedTitle` / `q9.unconfirmedBody`). It does not spin, and it does
 * not say "processing" — 03 §Q9's own decision applies here word for word: *a
 * hopeful dead end is worse than a clear one; it keeps a guest tapping instead
 * of waving.*
 *
 * ## What the design package draws, and the one thing it gets wrong
 *
 * The package draws this screen twice, and the difference between the two
 * drawings is the whole point of the surface's colour system:
 *
 *   * **Unverified** — a *white* card: headline, body, the amount **in ink, not
 *     green**, and the time the payment started. Then the ways out.
 *   * **Verified** — one Pandan card and nothing else: `เงินเข้าแล้ว` **in
 *     Pandan**, the operational payload (`ไม่ต้องแสดงสลิป` — no slip needed),
 *     and the amount in Pandan.
 *
 * The verified headline is green because the package draws it green and because
 * the ruling on this surface is that Pandan belongs on the *word*, not only on
 * the ground behind it. The unverified headline is ink, and so is every other
 * headline in this file — including the payment ReservePay says it took.
 *
 * Both are reproduced, with the tone taken from `paymentOutcome` rather than
 * decided here, so the green cannot appear for a payment the server has not
 * settled. Every other pending, loading and success state on this surface stays
 * ink, Palm Sugar or Butterfly Pea **precisely so this one moment reads as
 * different in kind** — which is why the design's own contradiction (a pill that
 * turns Pandan when a spice level is chosen) is not ported anywhere.
 *
 * The package's verified branch has **no buttons at all** — a dead end with no
 * route back to the bill, the status screen or the menu. That is a hole rather
 * than a decision, and it is filled below.
 *
 * The package also draws `ชำระที่เคาน์เตอร์` as a centred line that reads like a
 * third button and is not one. Resolved by drawing it as what it is: a sentence,
 * in body copy, not shaped like a control.
 *
 * ## ReservePay does not send anybody here
 *
 * `POKPOK_GUEST_RETURN_URL` is `https://<guest host>/status` — Q14's route, set
 * in `pokpok-infra`, not decidable from this repository. A returning payer lands
 * on the order-status screen, which knows nothing about a payment. So this file
 * also exports [`PaymentReturnNotice`], which Q14 renders when this phone has an
 * open attempt: one implementation, two mount points, no second copy of the
 * decision. The same pattern `Q02LanguageToggle` already uses for the language
 * switch.
 *
 * ## The polling, and why it is bounded
 *
 * A handful of re-reads with a widening gap, then it stops and offers a manual
 * check. Unbounded polling would be a battery drain in a dining room *and* a
 * lie: the state it would be waiting for cannot arrive on its own. It re-reads
 * on `visibilitychange` too, which is the real case — a guest opens their bank
 * app for PromptPay and comes back to the tab, and that is the app-switch-and-
 * return path 03 §Q9 asks for.
 *
 * There is no spinner anywhere in it. The one visible sign that a check is
 * running is the re-check button going quiet with `q9.checking` under it — a
 * state that says "asking again", never "nearly there".
 *
 * ## What is deliberately not here
 *
 *   * **No underpaid state.** Partial and split hosted payments are TBD in the
 *     contract, and the webhook refuses outright on an amount mismatch rather
 *     than recording a part-payment. There is nothing to render, so nothing is
 *     rendered — an invented "you paid ฿120 of ฿185" would be a number this
 *     surface made up about somebody's money.
 *   * **No refund path.** ReservePay does not support PromptPay refunds at all
 *     (01-pos.md §D10) and a guest-initiated refund request has no designed path
 *     anywhere. `q9.refundNote` points at a human, which is the only true thing
 *     to say. Flagged, not invented (03 §Q9 Open). It is shown on the
 *     unverified states only — the package puts it inside `payPending`, and a
 *     guest whose money has just landed is not asking.
 *   * **No live verification state.** There is no stream to hold: nothing in
 *     either store emits `checkout.updated`, so the SSE frame the contract
 *     defines for this never arrives. Polling is not a fallback here; it is the
 *     only mechanism.
 */

/** The copy for one outcome. Title and body are always both present — colour never carries it. */
const OUTCOME_COPY: Record<
  Exclude<PaymentOutcomeKind, 'unknown'>,
  {title: MessageKey; body: MessageKey}
> = {
  verified: {title: 'q9.verifiedTitle', body: 'q9.verifiedBody'},
  payment_taken: {title: 'q9.paymentTakenTitle', body: 'q9.paymentTakenBody'},
  failed: {title: 'q9.failedTitle', body: 'q9.failedBody'},
  expired: {title: 'q9.expiredTitle', body: 'q9.expiredBody'},
  unconfirmed: {title: 'q9.unconfirmedTitle', body: 'q9.unconfirmedBody'},
};

/**
 * How long to keep re-reading before settling into the honest answer.
 *
 * Three attempts over about twelve seconds. Long enough that a webhook arriving
 * while the guest is walking back from ReservePay is caught; short enough that
 * nobody watches a spinner for a state change the backend cannot currently make.
 */
const POLL_DELAYS_MS: readonly number[] = [2_000, 4_000, 6_000];

export default function Q09PaymentVerification() {
  const {t, ln, time} = useI18n();
  const {params} = useRouter();
  const client = useApi();
  const {session, status: sessionStatus} = useGuestSession();
  const {loadOrder} = useOrder();

  const orderId = params['orderId'] ?? '';
  const [attempt, setAttempt] = useState<CheckoutAttempt | null>(() =>
    orderId ? readAttemptFor(orderId) : null,
  );
  const [order, setOrder] = useState<Order | null>(null);
  const [checkout, setCheckout] = useState<CheckoutSession | null>(null);
  const [checking, setChecking] = useState(true);
  /** Set only when *nothing* could be read. Offline is weather, not a failure. */
  const [unreadable, setUnreadable] = useState(false);

  /**
   * One read of both authorities.
   *
   * The order first, because it is the one that can turn this screen green, and
   * the checkout session second, because it is the one that can say `failed`.
   * Each is caught separately: a checkout session that 404s (the record predates
   * this build, or storage is lying) must not blank an order that read fine.
   */
  const check = useCallback(async () => {
    if (!orderId || sessionStatus !== 'active') return;
    setChecking(true);
    let readSomething = false;

    try {
      setOrder(await loadOrder(orderId));
      readSomething = true;
    } catch (caught) {
      // A network failure keeps whatever is on screen. Offline is the supported
      // state of this product, not a fault (foundations §6).
      if (!(caught instanceof ApiError) || caught.kind !== 'network') readSomething = true;
    }

    const sessionId = readAttemptFor(orderId)?.checkout_session_id ?? null;
    if (sessionId) {
      try {
        setCheckout(await client.getCheckoutSession(sessionId));
        readSomething = true;
      } catch {
        // Same reasoning: the order is the authority on money and it may have
        // read fine. A failed session read narrows what can be said, not what
        // is true.
      }
    }

    setUnreadable(!readSomething);
    setChecking(false);
  }, [orderId, sessionStatus, loadOrder, client]);

  useEffect(() => {
    void check();
  }, [check]);

  // The bounded poll. Cleared the moment a terminal answer lands, so a settled
  // bill does not keep asking.
  const pollIndex = useRef(0);
  const [pollNonce, setPollNonce] = useState(0);
  const outcome = useMemo(
    () => paymentOutcome({order, session: checkout, attempt}),
    [order, checkout, attempt],
  );
  const terminal = outcome.kind === 'verified' || outcome.kind === 'payment_taken';

  useEffect(() => {
    if (terminal || pollIndex.current >= POLL_DELAYS_MS.length) return;
    const delay = POLL_DELAYS_MS[pollIndex.current]!;
    const id = setTimeout(() => {
      pollIndex.current += 1;
      setPollNonce((n) => n + 1);
      void check();
    }, delay);
    return () => clearTimeout(id);
  }, [terminal, check, pollNonce]);

  /**
   * The app-switch-and-return path 03 §Q9 asks for: the guest opened a banking
   * app to pay a PromptPay QR on the same phone that showed it, and came back.
   * Nothing else tells a page that happened.
   */
  useEffect(() => {
    const onVisible = () => {
      if (globalThis.document?.visibilityState === 'visible') void check();
    };
    globalThis.document?.addEventListener('visibilitychange', onVisible);
    return () => globalThis.document?.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  /**
   * A settled bill has nothing left to come back to, so the record is dropped.
   * Kept in every other state — including a failure — because a guest who
   * reloads must still be told what happened rather than "no payment started".
   */
  useEffect(() => {
    if (outcome.kind === 'verified') {
      clearAttempt();
      setAttempt(null);
    }
  }, [outcome.kind]);

  const venue = ln(session?.merchant_display_name);

  const topBar = (
    <TopBar
      leading={<BackLink to={ROUTES.status} label={t('common.back')} />}
      trailing={<LanguageToggle variant="link" />}
    />
  );

  /**
   * The ordinary shell. One column, no pinned footer, and the processor
   * disclosure held at the foot of the viewport by `margin-top: auto` — the
   * package's own shape for both halves of this screen.
   */
  const frame = (children: ReactNode) => (
    <Screen>
      <ScreenStyles />
      {topBar}
      <ScreenBody className="q9-body">
        <div className="pk-stack">
          <h1 className="pk-title">{t('q9.title')}</h1>
          {venue ? <p className="pk-muted">{venue}</p> : null}
        </div>
        {children}
        <p className="pk-hint q9-foot">{t('q17.processorDisclosure')}</p>
      </ScreenBody>
    </Screen>
  );

  const backToBill = (
    <ActionLink to={href(ROUTES.receipt, {orderId})} variant="tertiary" block>
      {t('q8.backToBill')}
    </ActionLink>
  );

  // --- no credential --------------------------------------------------------
  //
  // Drawn as the end of a road rather than as red text on a live screen: with no
  // session there is nothing to read, nothing to re-check and no bill to return
  // to. The only true next step is a person or a fresh scan.

  if (sessionStatus === 'idle' || sessionStatus === 'expired' || sessionStatus === 'invalid') {
    return (
      <Screen>
        <ScreenStyles />
        {topBar}
        <ScreenBody center>
          <TerminalNotice tone="error" glyph="!" title={t('error.session')}>
            <p className="pk-muted">{t('common.askStaff')}</p>
          </TerminalNotice>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          <p className="pk-hint q9-center">{t('q17.processorDisclosure')}</p>
        </ScreenFooter>
      </Screen>
    );
  }

  // Nothing was ever started from this phone. Not an error — a guest who opened
  // this URL directly, or who cleared storage. A plain white card and one way
  // out: no ring, no colour, because nothing happened.

  if (outcome.kind === 'unknown') {
    return frame(
      <>
        <Card>
          <p className="q9-headline">{t('q9.noAttempt')}</p>
        </Card>
        {backToBill}
      </>,
    );
  }

  const copy = OUTCOME_COPY[outcome.kind];
  const verified = outcome.kind === 'verified';
  const balanceSatang = order?.totals.balance_due_satang ?? attempt?.amount_satang ?? 0;
  const amountSatang = verified ? (order?.totals.total_satang ?? 0) : balanceSatang;

  return frame(
    <>
      {/*
        The one card, in one of two tones. `verified` comes from `paymentOutcome`
        — which reaches it only through `isVerifiedPaid`/`paymentTone`, the
        sanctioned deciders (D-021) — so this file never types the word green
        against a bill it decided about itself. Every other outcome, including a
        payment ReservePay says it took, is a white card.
      */}
      <Card as="section" tone={verified ? 'verified' : 'default'} role="status" aria-live="polite">
        <Stack>
          {/*
            The headline is Pandan on the verified state and ink on every other
            one. The green rides `verified`, which is the same `paymentOutcome`
            value that painted the card — so the word and its ground can never
            disagree, and neither can be reached for a bill the server has not
            settled.

            This is the owner's ruling that green belongs on the *word* rather
            than on the card alone, applied to the one word on this surface it
            can apply to. The package draws it the same way (`เงินเข้าแล้ว ✓` at
            `#0E7A4A`, the exact value behind `--pokpok-color-money-verified`).
          */}
          <h2 className={verified ? 'q9-headline q9-headline--verified' : 'q9-headline'}>
            {t(copy.title)}
          </h2>
          <p className="pk-muted">{t(copy.body)}</p>

          {order ? (
            <SplitRow>
              <span className="pk-muted">{verified ? t('common.total') : t('q8.amountDue')}</span>
              {/*
                `orderTotalTone` decides, and it is the only path to Pandan on
                this surface. It can only return `verified` for a bill the server
                settled — so this figure turns green when, and only when, the
                money is real. There is no tone meaning "probably paid" and this
                screen does not need one.
              */}
              <Money satang={amountSatang} tone={orderTotalTone(order)} size="title" />
            </SplitRow>
          ) : attempt ? (
            // The order could not be read. The amount this phone handed over is
            // still worth showing, and it is neutral by construction: nothing
            // that could say it was verified was reachable.
            <SplitRow>
              <span className="pk-muted">{t('q8.amountDue')}</span>
              <Money satang={attempt.amount_satang} size="title" />
            </SplitRow>
          ) : null}

          {attempt?.handed_off_at && time(attempt.handed_off_at) ? (
            <p className="pk-hint">{t('q9.startedAt', {time: time(attempt.handed_off_at)})}</p>
          ) : null}

          {unreadable ? <p className="pk-hint">{t('common.stale')}</p> : null}
        </Stack>
      </Card>

      {/*
        No spinner and no automatic retry once the bounded poll is done. The
        button is the honest version: pressing it asks again, and nothing about
        the wait implies an answer is on its way. While a check is running the
        button goes quiet and says so underneath — `disabledReason` renders that
        line and makes it the button's accessible description, so the reason is
        heard rather than only seen.
      */}
      {!terminal ? (
        <Button
          variant="secondary"
          block
          onClick={() => void check()}
          disabled={checking}
          disabledReason={t('q9.checking')}
        >
          {t('q9.recheck')}
        </Button>
      ) : null}

      {/*
        The screen's one solid Chili Red — and NOT on the state a successful
        payment actually lands in.

        The package reds `ชำระเงินอีกครั้ง` on the only result screen it draws,
        which is the unverified one. But `unconfirmed` is the value a guest who
        paid in full gets (see the header), ReservePay does not refund PromptPay
        at all, and a solid red is this surface's word for "this is the thing to
        do next". Urging a guest who may have already paid to pay again, in the
        one colour that means go, is how one bill gets paid twice with no way
        back — the same reasoning that leaves Q8's `reconcile` state with no
        button at all.

        So the red is spent only where paying again is unambiguously the forward
        action: ReservePay refused, or the ten minutes ran out. On `unconfirmed`
        the same control is still there, still one tap away, and merely does not
        shout — which is the honest state being the main state rather than an
        error case.
      */}
      {outcome.canPayAgain ? (
        <ActionLink
          to={href(ROUTES.pay, {orderId})}
          variant={
            outcome.kind === 'failed' || outcome.kind === 'expired' ? 'primary' : 'secondary'
          }
          block
        >
          {t('q9.payAgain')}
        </ActionLink>
      ) : null}

      {/* The package draws this as a centred line that looks like a third
          button. It is not one — there is nothing for it to do on a phone — so
          it is drawn as the sentence it always was. */}
      {!verified ? <p className="pk-muted q9-center">{t('q17.payAtCounter')}</p> : null}

      {/*
        The way out of a settled bill, which the package does not draw at all:
        its verified branch has no buttons and no route anywhere. The food is
        still coming, so the status screen is the honest next step.

        Deliberately NOT the solid red. The Pandan card above is the one moment
        the whole colour system exists to make different in kind, and a Chili Red
        button beside it competes with exactly that. A screen is allowed to have
        no primary action; it is not allowed to have two things shouting.
      */}
      {verified ? (
        <ActionLink to={ROUTES.status} variant="secondary" block>
          {t('q7.viewStatus')}
        </ActionLink>
      ) : null}

      {backToBill}

      {/*
        No refund path exists to offer. Point at a human and say so once — and
        only on a bill that is not settled, which is where the package draws it
        (inside `payPending`, never inside `payVerified`).

        It used to render unconditionally, which put "refunds are only available
        at the counter" directly under a Pandan card telling a guest their money
        arrived. Nothing on the verified state raises the question, so raising it
        there answers a worry the screen just finished removing — and hints at a
        path that does not exist (ReservePay does not refund PromptPay at all,
        01-pos.md §D10) at the one moment a guest has no reason to want one. Same
        gate as `q17.payAtCounter` one block up, for the same reason.
      */}
      {!verified ? <p className="pk-hint q9-center">{t('q9.refundNote')}</p> : null}
    </>,
  );
}

/**
 * The compact version of the above, for Q14.
 *
 * It exists because **ReservePay sends a returning payer to `/status`**, not to
 * this screen: `POKPOK_GUEST_RETURN_URL` is `https://<guest host>/status`, fixed
 * in `pokpok-infra`. Without this, a guest who has just handed over money lands
 * on a list of dishes with no acknowledgement that a payment happened anywhere
 * on the page.
 *
 * It renders **nothing** unless this phone has an open attempt, so it costs a
 * `localStorage` read on a screen that is not about money and adds no chrome to
 * the ordinary case. It reads storage on mount rather than taking a prop,
 * because it is mounted by a screen that does not know payments exist.
 *
 * Palm Sugar, through `Card tone="warning"`: unresolved and worth a look, and
 * emphatically not an error and not money. The figure inside carries no tone at
 * all — this component has no order to ask, and a payment nobody has verified is
 * ink like every other unremarkable amount.
 */
export function PaymentReturnNotice() {
  const {t, money} = useI18n();
  const [attempt] = useState<CheckoutAttempt | null>(() => readAttempt());

  if (!attempt) return null;

  /*
   * An expired attempt is not a pending one, and this notice used to show both.
   *
   * A ReservePay checkout stops accepting payment **ten minutes after creation,
   * fixed** — the caller cannot extend it, cancel it or call it back. The stored
   * record outlives it: only Q9 clears it, and a guest who closed the tab on
   * ReservePay's page never reaches Q9. So a phone that opened a checkout at
   * lunch was still being told "฿80 waiting to be confirmed" hours later, with a
   * button leading to a checkout that had run out before the food arrived.
   *
   * Reported by the owner as "this is stale, isn't it" — it was, in both senses.
   * The second sense is structural and does not need fixing here: since the bill
   * stopped offering online payment (2026-08-14) nothing on the surface mints a
   * new attempt, so this notice can now only ever be a leftover. It stays
   * because Q8 is still reachable by URL, and because the guest who genuinely is
   * mid-payment is exactly the one who must not lose the way back.
   */
  if (isExpired(attempt)) return null;

  return (
    <Card tone="warning" role="status">
      <ScreenStyles />
      <Stack>
        <p className="pk-strong">
          {t('q9.returnNotice', {amount: money(attempt.amount_satang)})}
        </p>
        {/* Stacked rather than sitting beside the sentence: at 16px with a 48px
            control there is no phone narrow enough for a row here that does not
            wrap into something worse. */}
        <ActionLink
          to={href(ROUTES.paymentReturn, {orderId: attempt.order_id})}
          variant="secondary"
          block
        >
          {t('q9.returnNoticeAction')}
        </ActionLink>
      </Stack>
    </Card>
  );
}

/**
 * The four rules this screen needs and the shared sheet does not carry.
 *
 * `href` + `precedence` are React 19's stylesheet de-duplication, the same
 * mechanism `UiStyles` uses: rendered from four different branches of this file
 * — including from inside Q14, which mounts `PaymentReturnNotice` — and hoisted
 * into `<head>` exactly once. Tokens only.
 */
function ScreenStyles() {
  return (
    <style href="pokpok-q9" precedence="pokpok-ui">
      {CSS}
    </style>
  );
}

const CSS = `
/* One column, disclosure pushed to the foot of the viewport, no pinned bar —
   the package's own shape for this screen. */
.q9-body {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}
.q9-foot {
  margin-top: auto;
  padding-top: var(--pokpok-space-4);
  text-align: center;
}
.q9-center {
  text-align: center;
}
/* The card headline. Body size, weight 700 — the design drew it at 14px/700 and
   the 16px floor takes the size, not the weight. An unclassed h2 would take the
   user agent's 1.5em and quietly introduce a fourth type size. */
.q9-headline {
  font-size: var(--text-body-size);
  font-weight: 700;
  margin: 0;
}
/* MONEY RULE: the only green in this sheet, and it is only ever applied by the
   verified branch of paymentOutcome — the same value that gives the card its
   tone. It rides on the card's own decision rather than on a class a future
   caller could hand it, so there is no way to paint this headline Pandan on a
   bill the server has not settled. Same token as Money tone="verified", so the
   word and the figure below it are one colour and not two greens.
   (No backticks in this sheet: it is a template literal.) */
.q9-headline--verified {
  color: var(--pokpok-color-money-verified);
}
`;
